// src/pages/QrTableOrderPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatStoredMoney } from "../utils/currency";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaTimesCircle,
  FaSyncAlt,
  FaPrint,
} from "react-icons/fa";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
};

const TABS = [
  { key: "pending", label: "Pending", color: COLOR.amber, typeValue: -1 },
  { key: "accept", label: "Accepted", color: COLOR.green, typeValue: 1 },
  { key: "decline", label: "Declined", color: COLOR.red, typeValue: 2 },
];
const QR_ORDER_NUMBER_MAP_KEY = "qr_completed_order_numbers";

const getAcceptedOrderNumber = (item) => {
  const apiOrderNumber =
    item?.ordercode ||
    item?.order_code ||
    item?.billno ||
    item?.bill_no ||
    item?.completed_ordercode ||
    item?.completed_order_code ||
    item?.order?.ordercode ||
    item?.order?.order_code ||
    item?.order?.billno ||
    item?.orderdetails?.ordercode ||
    item?.orderdetails?.order_code ||
    item?.orderdetails?.billno;
  if (apiOrderNumber !== undefined && apiOrderNumber !== null && String(apiOrderNumber).trim()) {
    return String(apiOrderNumber).trim();
  }

  try {
    const mapping = JSON.parse(localStorage.getItem(QR_ORDER_NUMBER_MAP_KEY) || "{}");
    const qrOrderId = String(item?.qrorderid ?? item?.id ?? "");
    if (qrOrderId && mapping?.[qrOrderId]) return String(mapping[qrOrderId]);
  } catch {
    // Fall back to the QR order id below.
  }
  return String(item?.qrorderid ?? item?.id ?? "-");
};

const getLinkedCompletedOrderId = (item) =>
  item?.completed_order_id ||
  item?.completed_orderid ||
  item?.pos_order_id ||
  item?.pos_orderid ||
  item?.order?.id ||
  item?.order?.orderid ||
  item?.orderdetails?.id ||
  item?.orderdetails?.orderid ||
  null;

const normalizeOrderNumber = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return /^\d+$/.test(text) ? text.replace(/^0+(?=\d)/, "") : text.toLowerCase();
};

const dateForApi = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return today();
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const QrTableOrderPage = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedTab, setSelectedTab] = useState("pending");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openingPrintId, setOpeningPrintId] = useState("");
  const [fromDate, setFromDate] = useState(() => today());
  const [toDate, setToDate] = useState(() => today());

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    if (userId) fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedTab, fromDate, toDate]);

  const fetchOrders = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const tab = TABS.find((t) => t.key === selectedTab);
      const typeValue = tab?.typeValue ?? -1;
      const res = await fetch(
        API_ENDPOINTS.LIST_ORDER_FROM_QR,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: userId,
            fromdate: fromDate,
            todate: toDate,
            type: typeValue,
          }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load orders");
      setOrders(Array.isArray(json.list) ? json.list : []);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Unable to load orders");
      setOrders([]);
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      setOrders((prev) => prev.filter((o) => o.id !== orderId)); // optimistic remove
      const res = await fetch(
        `${API_BASE_URL}/acceptreject/qrorder`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ qrorderid: orderId, accept: status }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.message || "Status update failed");
      }
    } catch (e) {
      toast.error(e.message || "Unable to update status");
      fetchOrders(); // rollback
    }
  };

  const openCompletedBill = async (item) => {
    const qrOrderId = String(item?.qrorderid ?? item?.id ?? "");
    if (openingPrintId) return;
    setOpeningPrintId(qrOrderId);
    try {
      const linkedId = getLinkedCompletedOrderId(item);
      if (linkedId) {
        navigate(`/print/${encodeURIComponent(linkedId)}`);
        return;
      }

      const orderNumber = getAcceptedOrderNumber(item);
      const normalizedTarget = normalizeOrderNumber(orderNumber);
      if (!normalizedTarget) throw new Error("Completed order number is unavailable.");

      const orderDate = dateForApi(item?.created_at);
      const response = await fetch(API_ENDPOINTS.GET_ORDER_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          fromdate: orderDate,
          todate: orderDate,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.message || "Unable to find bill.");

      const matchedOrder = (Array.isArray(result?.list) ? result.list : []).find(
        (order) =>
          [order?.ordercode, order?.order_code, order?.billno, order?.code].some(
            (value) => normalizeOrderNumber(value) === normalizedTarget
          )
      );
      const actualOrderId = matchedOrder?.id || matchedOrder?.orderid || matchedOrder?.order_id;
      if (!actualOrderId) {
        throw new Error(`Bill #${orderNumber} was not found in completed orders.`);
      }

      navigate(`/print/${encodeURIComponent(actualOrderId)}`, {
        state: { order: matchedOrder },
      });
    } catch (error) {
      toast.error(error?.message || "Unable to open bill for printing.");
    } finally {
      setOpeningPrintId("");
    }
  };

  const counts = useMemo(() => {
    return {
      total: orders.length,
    };
  }, [orders]);

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const statusChip = (tabKey) => {
    if (tabKey === "accept")
      return { bg: "#dcfce7", fg: "#166534", label: "Accepted" };
    if (tabKey === "decline")
      return { bg: "#fee2e2", fg: "#991b1b", label: "Declined" };
    return { bg: "#fff7ed", fg: "#9a3412", label: "Pending" };
  };

  const fmtDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("en-GB").replace(/\//g, "-");
    } catch {
      return d;
    }
  };
  const fmtTime = (d) => {
    try {
      return new Date(d).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };
  const fmtINR = formatStoredMoney;

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2400} />

      <TopBar>
        <div>
          <Title>QR Table Orders</Title>
          <Sub>Manage scan orders from customers</Sub>
          <PillRow>
            <Pill>Total: {counts.total}</Pill>
            <Pill $muted>
              Range: {fromDate} → {toDate}
            </Pill>
          </PillRow>
        </div>
        <Actions>
          <DateInput>
            <FaCalendarAlt />
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </DateInput>
          <DateInput>
            <FaCalendarAlt />
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </DateInput>
          <PrimaryBtn onClick={() => fetchOrders({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </TopBar>

      <Tabs>
        {TABS.map((t) => {
          const isActive = selectedTab === t.key;
          return (
            <TabBtn
              key={t.key}
              $active={isActive}
              onClick={() => setSelectedTab(t.key)}
            >
              <Dot style={{ background: t.color }} />
              <span>
                {t.label}
                {isActive ? ` (${orders.length})` : ""}
              </span>
            </TabBtn>
          );
        })}
      </Tabs>

      {loading ? (
        <Centered>Loading…</Centered>
      ) : orders.length === 0 ? (
        <Centered>No orders found for this range.</Centered>
      ) : (
        <List>
          {orders.map((item) => {
            const chip = statusChip(selectedTab);
            const isExpanded = !!expanded[item.id];
            const items = Array.isArray(item.items) ? item.items : [];
            const totals = items.reduce(
              (acc, prod) => {
                acc.qty += parseInt(prod?.qty || 0, 10);
                acc.amount +=
                  parseInt(prod?.qty || 0, 10) * parseFloat(prod?.price || 0);
                return acc;
              },
              { qty: 0, amount: 0 }
            );
            const displayedOrderNumber =
              selectedTab === "accept"
                ? getAcceptedOrderNumber(item)
                : String(item?.qrorderid ?? item?.id ?? "-");

            return (
              <Card key={item.id}>
                <CardHead>
                  <div>
                    <CardTitle>Table {item.table_no || "-"}</CardTitle>
                    <CardMeta>
                      Order #{displayedOrderNumber} · {fmtDate(item.created_at)}{" "}
                      {fmtTime(item.created_at)}
                    </CardMeta>
                  </div>
                  <StatusChip style={{ background: chip.bg, color: chip.fg }}>
                    {chip.label}
                  </StatusChip>
                </CardHead>

                <CardMetaRow>
                  <MetaItem>{items.length} items</MetaItem>
                  <MetaItem>Total Qty: {totals.qty}</MetaItem>
                  <MetaItem>{fmtINR(totals.amount)}</MetaItem>
                </CardMetaRow>

                <Divider />

                <ExpandRow onClick={() => toggleExpand(item.id)}>
                  <span>{isExpanded ? "Hide items" : "View items"}</span>
                  {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </ExpandRow>

                {isExpanded && items.length > 0 && (
                  <Items>
                    {items.map((prod, idx) => {
                      const total =
                        parseInt(prod?.qty || 0, 10) *
                        parseFloat(prod?.price || 0);
                      return (
                        <ItemRow key={idx}>
                          <ItemName>{prod?.product?.name || "-"}</ItemName>
                          <ItemQty>
                            {prod?.qty} × {fmtINR(prod?.price)}
                          </ItemQty>
                          <ItemAmt>{fmtINR(total)}</ItemAmt>
                        </ItemRow>
                      );
                    })}
                    <ItemRow className="total">
                      <ItemName>Totals</ItemName>
                      <ItemQty>Qty {totals.qty}</ItemQty>
                      <ItemAmt>{fmtINR(totals.amount)}</ItemAmt>
                    </ItemRow>
                  </Items>
                )}

                {selectedTab === "pending" && (
                  <ActionRow>
                    <PrimaryBtn small onClick={() => updateStatus(item.id, 1)}>
                      <FaCheckCircle /> Accept
                    </PrimaryBtn>
                    <DangerBtn small onClick={() => updateStatus(item.id, 2)}>
                      <FaTimesCircle /> Decline
                    </DangerBtn>
                  </ActionRow>
                )}
                {selectedTab === "accept" && (
                  <ActionRow>
                    <PrintBtn
                      type="button"
                      disabled={!!openingPrintId}
                      onClick={() => openCompletedBill(item)}
                    >
                      <FaPrint /> {openingPrintId === String(item?.qrorderid ?? item?.id ?? "") ? "Opening Bill..." : "Print Bill"}
                    </PrintBtn>
                  </ActionRow>
                )}
              </Card>
            );
          })}
        </List>
      )}
    </Page>
  );
};

export default QrTableOrderPage;

/* ---------------- helpers ---------------- */
const today = () => {
  const d = new Date();
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------------- styled ---------------- */
const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
  font-family: Inter, system-ui, Segoe UI, Roboto, "Helvetica Neue", Arial;
`;
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
  margin-top: 2px;
`;
const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;
const Pill = styled.span`
  background: ${({ $muted }) => ($muted ? "#eef2ff" : "#dcfce7")};
  color: ${({ $muted }) => ($muted ? COLOR.text : "#166534")};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;
const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ small }) => (small ? "8px 10px" : "10px 12px")};
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(var(--app-primary-rgb), 0.16);
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${COLOR.red};
  box-shadow: 0 10px 24px rgba(220, 38, 38, 0.16);
`;
const PrintBtn = styled(PrimaryBtn)`
  background: #0f766e;
  box-shadow: 0 10px 24px rgba(15, 118, 110, 0.18);
`;
const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
`;
const TabBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${COLOR.line};
  background: ${({ $active }) => ($active ? "#fff" : "#f3f6fb")};
  color: ${COLOR.text};
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0 8px 18px rgba(0,0,0,0.08)" : "none"};
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
`;

const List = styled.div`
  display: grid;
  gap: 10px;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.05);
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;
const CardTitle = styled.div`
  font-weight: 900;
  color: ${COLOR.text};
  font-size: 1rem;
`;
const CardMeta = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
`;
const StatusChip = styled.span`
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;
const CardMetaRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
  color: ${COLOR.muted};
  font-size: 13px;
`;
const MetaItem = styled.span`
  background: #f3f6fb;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid ${COLOR.line};
`;
const Divider = styled.div`
  height: 1px;
  background: ${COLOR.line};
  margin: 10px 0;
`;
const ExpandRow = styled.button`
  border: none;
  background: #f9fafb;
  border-radius: 10px;
  padding: 8px 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: ${COLOR.text};
  font-weight: 700;
`;
const Items = styled.div`
  margin-top: 8px;
  display: grid;
  gap: 6px;
`;
const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f8fafc;
  &.total {
    background: #eef2ff;
    font-weight: 800;
  }
`;
const ItemName = styled.div`
  font-weight: 700;
  color: ${COLOR.text};
  min-width: 0;
`;
const ItemQty = styled.div`
  color: ${COLOR.muted};
  font-weight: 700;
`;
const ItemAmt = styled.div`
  text-align: right;
  font-weight: 800;
  color: ${COLOR.text};
`;
const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const Centered = styled.div`
  display: grid;
  place-items: center;
  min-height: 30vh;
  color: ${COLOR.muted};
`;
